/* additionalCandidates.js
 *
 * Pure, store-free derivation of the structures the multi-protein simulation form
 * offers as "additional" targets: the loaded structures plus the projectState target
 * list (structures of interest + curated GroupTargets / kinase panels). Kept out of
 * the component so the bucketing + dedup is unit-testable without redux or a render.
 *
 * Tests in test/bmaps/additionalCandidates.test.js.
 */
import { isGroupEntry, asIndividualTarget, getEntryMapCases } from 'BMapsRedux/projectState/entry';
import { structureKey } from '../../../model/structureRef';

/**
 * @typedef {{ groupId: string, displayName: string, members: import('BMapsModel').MapCase[] }}
 *   CandidateGroup
 * @typedef {{
 *   loaded: import('BMapsModel').MapCase[],
 *   groups: CandidateGroup[],
 *   sois: import('BMapsModel').MapCase[],
 * }} AdditionalCandidates
 */

/**
 * Bucket the additional-target candidates for the multi-protein sim form:
 *  - loaded: loaded structures except the primary (individual checkboxes).
 *  - groups: curated GroupTargets -> { groupId, displayName, members } (one select-all
 *            row each; panel members are typically NOT loaded).
 *  - sois:   individual structure-of-interest targets that are not already loaded.
 * Deduped by structureKey: a structure that is both loaded and an individual target
 * appears once (in `loaded`); the primary is excluded from every bucket. Group members
 * are left intact (a panel is offered as a whole; overlap is resolved at submission).
 * @param {import('BMapsModel').MapCase[]} loadedProteins
 * @param {?import('BMapsModel').MapCase} selectedProtein
 * @param {import('BMapsRedux/projectState/slice').TargetEntry[]} targetList
 * @returns {AdditionalCandidates}
 */
export function buildAdditionalCandidates(loadedProteins, selectedProtein, targetList) {
    const loaded = (loadedProteins ?? []).filter((p) => p !== selectedProtein);

    const seen = new Set(loaded.map(structureKey));
    const primaryKey = structureKey(selectedProtein);
    if (primaryKey) seen.add(primaryKey);

    const list = targetList ?? [];
    const groups = list.filter(isGroupEntry).map((entry) => ({
        groupId: entry.groupId,
        displayName: entry.displayName ?? entry.groupId,
        members: getEntryMapCases(entry),
    }));

    const sois = [];
    for (const entry of list) {
        const individual = asIndividualTarget(entry);
        if (!individual) continue;
        const key = structureKey(individual.mapCase);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        sois.push(individual.mapCase);
    }

    return { loaded, groups, sois };
}
